import {useMutation} from "@tanstack/react-query"
import {Radio} from "antd"
import {BaseButton, BasePopup} from "components"
import {handleError, toastSuccess} from "helpers"
import {useRetailersGatewayCollect} from "hooks"
import {usePaymentProviderConfigs} from "hooks/usePaymentProviderConfigs"
import {RetailersGatewayPaymentType} from "hooks/useRetailersGatewayCollect"
import {useEffect, useState} from "react"
import {useTranslation} from "react-i18next"
import {studentPaymentService} from "services"
import {RetailersGatewayPaymentDetails} from "types/student-account/student-payment"
import styles from "./PaymentMethods.module.css"

type Props = {
    isShow: boolean
    onClose: () => void
    onSuccess: () => void
    profileId: number
}

/**
 * Saves a card or bank account to the student's Retailers Gateway Customer Vault without charging
 * it. Details are entered in the gateway-hosted Collect.js lightbox.
 */
const RetailersGatewayPaymentMethodPopup = ({isShow, onClose, onSuccess, profileId}: Props) => {
    const {t} = useTranslation(["payment", "common"])
    const {retailersGatewayProviderConfig} = usePaymentProviderConfigs(profileId)
    const {isReady, error: collectError, openPaymentForm} = useRetailersGatewayCollect(retailersGatewayProviderConfig)
    const [paymentType, setPaymentType] = useState<RetailersGatewayPaymentType>("card")

    useEffect(() => {
        if (collectError) handleError(collectError)
    }, [collectError])

    const {isLoading, mutate: attach} = useMutation({
        mutationFn: async ({
            paymentToken,
            paymentDetails
        }: {
            paymentToken: string
            paymentDetails: RetailersGatewayPaymentDetails
        }) => studentPaymentService.retailersGatewayAttachPaymentMethod(profileId, paymentToken, paymentDetails),
        onError: (error) => handleError(error),
        onSuccess: () => {
            toastSuccess(t("msg.paymentMethod.addedSuccessfully"))
            onSuccess()
        }
    })

    const handleAdd = () => {
        try {
            openPaymentForm(paymentType, (paymentToken, paymentDetails) => attach({paymentToken, paymentDetails}))
        } catch (error) {
            handleError(error)
        }
    }

    return (
        <BasePopup isShow={isShow} onClose={onClose} leftIcon="CREDIT_CARD" width="40vw">
            <h1 className={styles.title}>{t("title.addPaymentMethod")}</h1>
            <Radio.Group value={paymentType} onChange={(e) => setPaymentType(e.target.value)}>
                <Radio value="card">Card</Radio>
                <Radio value="ach">Bank Account (ACH)</Radio>
            </Radio.Group>
            <BaseButton
                size="small"
                title={t("common:action.add")}
                disabled={!isReady || isLoading}
                loading={isLoading}
                onClick={handleAdd}
                className="w-full mt-24"
            />
        </BasePopup>
    )
}

export default RetailersGatewayPaymentMethodPopup
